import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { consultationApi } from '../api/consultationApi';
import type {
  CreateConsultationDto,
  UpdateConsultationDto,
  ConsultationResponseDto,
} from '../types/consultation';

export const useConsultation = (appointmentId: string) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // Get consultation data
  const {
    data: consultation,
    isLoading,
    error,
    refetch,
  } = useQuery<ConsultationResponseDto | null, Error>({
    queryKey: ['consultation', appointmentId],
    queryFn: async () => {
      try {
        return await consultationApi.getByAppointmentId(appointmentId);
      } catch (error: any) {
        // Return null if the consultation doesn't exist (404) - this is normal for new consultations
        if (error?.response?.status === 404) {
          return null;
        }
        // Re-throw other errors
        throw error;
      }
    },
    retry: (failureCount: number, error: any) => {
      // Don't retry on 404 errors
      if (error?.response?.status === 404) {
        return false;
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
    enabled: !!appointmentId,
  });

  // Create consultation mutation
  const createMutation = useMutation({
    mutationFn: async (data: CreateConsultationDto) => {
      console.log('useConsultation - create mutation called with:', { 
        data,
        appointmentId 
      });

      // Create the consultation
      console.log('Sending to API - create consultation:', data);
      const consultation = await consultationApi.create(appointmentId, data);
      
      console.log('API Response - created consultation:', consultation);
      return consultation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', appointmentId] });
      enqueueSnackbar('The consultation has been created successfully.', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating consultation: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  // Update consultation mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateConsultationDto & { id: string }) => {
      console.log('useConsultation - update mutation called with:', { 
        data,
        appointmentId 
      });

      // Update the consultation
      console.log('Sending to API - update consultation:', data);
      const consultation = await consultationApi.update(appointmentId, data);
      
      console.log('API Response - updated consultation:', consultation);
      return consultation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', appointmentId] });
      enqueueSnackbar('The consultation has been updated successfully.', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating consultation: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  // Delete consultation mutation
  const deleteMutation = useMutation({
    mutationFn: () => consultationApi.delete(appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultation', appointmentId] });
      enqueueSnackbar('The consultation has been deleted successfully.', {
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error deleting consultation: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  return {
    consultation: consultation || undefined,
    isLoading,
    error,
    createConsultation: async (data: CreateConsultationDto) => {
      const result = await createMutation.mutateAsync(data);
      return result as ConsultationResponseDto;
    },
    updateConsultation: async (data: UpdateConsultationDto & { id: string }) => {
      const result = await updateMutation.mutateAsync(data);
      return result as ConsultationResponseDto;
    },
    deleteConsultation: async () => {
      await deleteMutation.mutateAsync(undefined, {
        onSuccess: () => {
          // Invalidate the query to refetch the consultation data
          queryClient.invalidateQueries({ queryKey: ['consultation', appointmentId] });
        },
      });
    },
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    refetch: async () => {
      const result = await refetch();
      return { 
        data: result.data || undefined, 
        status: result.status === 'success' ? 'success' as const : 'error' as const 
      };
    },
  };
};
